import { CheckCircle, Clock, AlertCircle } from 'lucide-react'

import { logger } from './logger'

export const getStatusIcon = (status: string) => {
  const normalizedStatus = status?.toLowerCase()
  switch (normalizedStatus) {
    case 'completed':
    case 'settled':
      return <CheckCircle className="w-5 h-5 text-green-500" />
    case 'processing':
    case 'initiated':
      return <Clock className="w-5 h-5 text-blue-500" />
    case 'pending':
      return <AlertCircle className="w-5 h-5 text-yellow-500" />
    case 'failed':
    case 'cancelled':
      return <AlertCircle className="w-5 h-5 text-red-500" />
    default:
      return <Clock className="w-5 h-5 text-gray-400" />
  }
}

export const getStatusColor = (status: string) => {
  const normalizedStatus = status?.toLowerCase()
  switch (normalizedStatus) {
    case 'completed':
    case 'settled':
      return 'text-green-600 bg-green-50 border-green-200'
    case 'processing':
    case 'initiated':
      return 'text-blue-600 bg-blue-50 border-blue-200'
    case 'pending':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    case 'failed':
    case 'cancelled':
      return 'text-red-600 bg-red-50 border-red-200'
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200'
  }
}

export const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const formatCurrency = (amount: number, currency: string) => {
  return `${amount?.toFixed(2)} ${currency}`
}

export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    logger.error('Failed to copy to clipboard', err)
    return false
  }
}

/**
 * Format settlement time in a human-readable format
 * Converts minutes to hours or days when appropriate
 * @param min - Minimum settlement time
 * @param max - Maximum settlement time
 * @param unit - Time unit (minutes, hours, days)
 * @returns Formatted settlement time string
 */
export const formatSettlementTime = (
  min: number,
  max: number,
  unit: string
): string => {
  const unitLower = unit.toLowerCase()

  if (unitLower === 'minutes') {
    // Convert minutes to hours or days if needed
    if (max >= 1440) {
      // 1440 minutes = 1 day
      const minDays = Math.floor(min / 1440)
      const maxDays = Math.ceil(max / 1440)
      if (minDays === maxDays) {
        return `${maxDays} day${maxDays > 1 ? 's' : ''}`
      }
      // If min is 0, only show max (e.g., "3 days" instead of "0-3 days")
      if (minDays === 0) {
        return `${maxDays} day${maxDays > 1 ? 's' : ''}`
      }
      return `${minDays}-${maxDays} days`
    } else if (max >= 60) {
      // Convert to hours
      const minHours = Math.floor(min / 60)
      const maxHours = Math.ceil(max / 60)
      if (minHours === maxHours) {
        return `${maxHours} hour${maxHours > 1 ? 's' : ''}`
      }
      // If min is 0, only show max (e.g., "2 hours" instead of "0-2 hours")
      if (minHours === 0) {
        return `${maxHours} hour${maxHours > 1 ? 's' : ''}`
      }
      return `${minHours}-${maxHours} hours`
    }
    // Keep as minutes if less than 60
    if (min === max) {
      return `${max} minute${max > 1 ? 's' : ''}`
    }
    // If min is 0, only show max (e.g., "30 minutes" instead of "0-30 minutes")
    if (min === 0) {
      return `${max} minute${max > 1 ? 's' : ''}`
    }
    return `${min}-${max} minutes`
  }

  // For other units, just format as-is
  if (min === max) {
    return `${max} ${unitLower === 'hours' ? 'hour' : unitLower === 'days' ? 'day' : unitLower}${max > 1 ? 's' : ''}`
  }

  // If min is 0, only show max (e.g., "2 days" instead of "0-2 days")
  if (min === 0) {
    return `${max} ${unitLower}`
  }

  return `${min}-${max} ${unitLower}`
}

/**
 * Get user's fee display preference from localStorage
 * @returns 'USDC' or 'CAD'
 */
export const getFeeDisplayPreference = (): 'USDC' | 'CAD' => {
  return (
    (localStorage.getItem('feeDisplayCurrency') as 'USDC' | 'CAD') || 'USDC'
  )
}

/**
 * Format fee amount based on user preference and available conversion rate
 * @param feeAmount - The fee amount (usually in USDC)
 * @param feeCurrency - The original fee currency
 * @param displayCurrency - User's preferred display currency ('USDC' | 'CAD')
 * @param usdcToCadRate - Current USDC to CAD conversion rate (optional)
 * @returns Formatted fee string with currency symbol
 */
export const formatFeeAmount = (
  feeAmount: number,
  feeCurrency: string,
  displayCurrency: 'USDC' | 'CAD' = 'USDC',
  usdcToCadRate?: number
): string => {
  // If displaying in original currency or no conversion rate available
  if (displayCurrency === feeCurrency || !usdcToCadRate) {
    const symbol = feeCurrency === 'CAD' ? '$' : ''
    return `${symbol}${feeAmount.toFixed(2)} ${feeCurrency}`
  }

  // Convert USDC to CAD if requested and rate is available
  if (feeCurrency === 'USDC' && displayCurrency === 'CAD' && usdcToCadRate) {
    const cadAmount = feeAmount / usdcToCadRate
    return `$${cadAmount.toFixed(2)} CAD`
  }

  // Convert CAD to USDC if requested and rate is available
  if (feeCurrency === 'CAD' && displayCurrency === 'USDC' && usdcToCadRate) {
    const usdcAmount = feeAmount * usdcToCadRate
    return `${usdcAmount.toFixed(2)} USDC`
  }

  // Fallback to original currency
  const symbol = feeCurrency === 'CAD' ? '$' : ''
  return `${symbol}${feeAmount.toFixed(2)} ${feeCurrency}`
}
